import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Container, Grid, Card, CardContent, Typography } from '@mui/material';
import { useOrder } from '../../state/OrderContext';
import { getTableStatus } from '../../state/tableStatus';
import { useLayout } from '../../state/LayoutContext';
import type { TableStatus } from '../../shared/types';

const STATUS_BG: Record<TableStatus, string> = {
  frei: '#E8E8E8',
  offen: '#FFCDD2',
  unbezahlt: '#FFF9C4',
};
const STATUS_TEXT: Record<TableStatus, string> = {
  frei: '#9E9E9E',
  offen: '#7F0000',
  unbezahlt: '#5D4037',
};
const STATUS_SUB: Record<TableStatus, string> = {
  frei: '#BDBDBD',
  offen: '#C62828',
  unbezahlt: '#8D6E63',
};
const STATUS_SHADOW: Record<TableStatus, string> = {
  frei: 'none',
  offen: '0 2px 8px rgba(198,40,40,0.25)',
  unbezahlt: '0 2px 8px rgba(255,193,7,0.3)',
};

interface TablesScreenProps {
  onSelectTable: (tableNumber: number) => void;
}

const TablesScreen: React.FC<TablesScreenProps> = ({ onSelectTable }) => {
  const { t } = useTranslation();
  const { state } = useOrder();
  const { areas, allTables } = useLayout();
  const [activeZone, setActiveZone] = useState<string>('Alle');

  const statusLabel: Record<TableStatus, string> = {
    frei: t('tables.statusFree'),
    offen: t('tables.statusInKitchen'),
    unbezahlt: t('tables.statusUnpaid'),
  };

  const visibleTables = useMemo(
    () => allTables.filter((table) => activeZone === 'Alle' || table.zoneId === activeZone),
    [activeZone, allTables]
  );

  return (
    <Container maxWidth="md" sx={{ py: 2 }}>
      <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1 }}>
        <Chip
          label={t('tables.zoneAll')}
          onClick={() => setActiveZone('Alle')}
          sx={{
            backgroundColor: activeZone === 'Alle' ? 'primary.main' : '#F0F0F0',
            color: activeZone === 'Alle' ? '#fff' : '#616161',
            fontWeight: 600,
          }}
        />
        {areas.map((area) => (
          <Chip
            key={area.id}
            label={area.name}
            onClick={() => setActiveZone(area.id)}
            sx={{
              backgroundColor: activeZone === area.id ? 'primary.main' : '#F0F0F0',
              color: activeZone === area.id ? '#fff' : '#616161',
              fontWeight: 600,
              flexShrink: 0,
            }}
          />
        ))}
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', py: 1.5 }}>
        {(['frei', 'offen', 'unbezahlt'] as TableStatus[]).map((status) => (
          <Box key={status} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '3px', backgroundColor: STATUS_BG[status] }} />
            <Typography sx={{ fontSize: '0.75rem', color: '#9E9E9E' }}>{statusLabel[status]}</Typography>
          </Box>
        ))}
      </Box>

      <Grid container spacing={1.5}>
        {visibleTables.map((table) => {
          const status = getTableStatus(state.tables[table.num]);
          return (
            <Grid size={{ xs: 6, sm: 4, md: 3 }} key={table.num}>
              <Card
                onClick={() => onSelectTable(table.num)}
                sx={{
                  cursor: 'pointer',
                  backgroundColor: STATUS_BG[status],
                  boxShadow: STATUS_SHADOW[status],
                  border: 'none',
                }}
              >
                <CardContent
                  sx={{
                    textAlign: 'center',
                    py: 2.5,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.25,
                    '&:last-child': { pb: 2.5 },
                  }}
                >
                  <Typography sx={{ fontWeight: 800, fontSize: '1.3rem', color: STATUS_TEXT[status] }}>
                    {table.num}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: '0.65rem',
                      color: STATUS_SUB[status],
                      letterSpacing: '0.05em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {statusLabel[status]}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Container>
  );
};

export default TablesScreen;
